var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { ArrowUpward, QuestionAnswer } from '@mui/icons-material';
import { alpha, Avatar, Box, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Typography } from '@mui/material';
import { setSelectedItem, setSortSelectedItem } from 'app/actions/issue';
import { Autosizer, Scrollbar } from 'app/components';
import { selectedItemIssueSelector, sortItemsIssueSelector, sortSelectedItemIssueSelector } from 'app/selectors/issue';
import React, { createContext, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';
const IssueListContext = createContext(null);
const IssueList = () => {
    const dispatch = useDispatch();
    const selectedItem = useSelector(selectedItemIssueSelector);
    const sortSelectedItem = useSelector(sortSelectedItemIssueSelector);
    const sortItems = useSelector(sortItemsIssueSelector);
    const handleItemClick = (value) => dispatch(setSelectedItem(value));
    const handleSortClick = (value) => dispatch(setSortSelectedItem(value));
    return (React.createElement(Box, { component: 'div', sx: {
            width: '100%',
            height: '100%',
            '.simplebar-offset > div': {
                '&::-webkit-scrollbar': {
                    width: 0,
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                    boxShadow: 'none',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            },
            '.simplebar-horizontal': {
                right: '10px',
            },
            '.simplebar-vertical': {
                top: '30px',
                bottom: '0',
            },
        } },
        React.createElement(Autosizer, null, ({ height, width }) => (React.createElement(Box, { sx: { height, width } },
            React.createElement(IssueListContext.Provider, { value: {
                    onSortClick: handleSortClick,
                    sortSelectedItem,
                } },
                React.createElement(Scrollbar, null, ({ scrollableNodeRef, contentNodeRef }) => (React.createElement(FixedSizeList, { height: height, itemSize: 24, itemCount: sortItems.length, innerRef: contentNodeRef, outerRef: scrollableNodeRef, innerElementType: IssueListInnerElementType, itemData: {
                        items: sortItems,
                        onClick: handleItemClick,
                        selectedItem,
                    } }, IssueListItemRender)))))))));
};
const IssueListInnerElementType = forwardRef((_a, ref) => {
    var { children, style } = _a, rest = __rest(_a, ["children", "style"]);
    return (React.createElement(IssueListContext.Consumer, null, (context) => {
        if (!context) {
            return null;
        }
        const { onSortClick, sortSelectedItem } = context;
        return (React.createElement(List, Object.assign({ ref: ref, sx: {
                height: style === null || style === void 0 ? void 0 : style.height,
                minHeight: '100%',
            } }, rest),
            React.createElement(ListItem, { disablePadding: true, sx: (theme) => ({
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    minHeight: '100%',
                    zIndex: '2',
                    pointerEvents: 'none',
                    padding: theme.spacing(0),
                    '.MuiListItemText-root': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        height: '100%',
                        position: 'relative',
                        flex: 'none',
                        '&:before': {
                            content: '""',
                            position: 'absolute',
                            top: '0',
                            right: '0',
                            height: '100%',
                            bgcolor: theme.palette.divider,
                            width: '1px',
                            zIndex: '1',
                            pointerEvents: 'none',
                        },
                        '&:last-of-type': {
                            flex: '1',
                            '&:before': {
                                display: 'none',
                            },
                        },
                    },
                }) },
                React.createElement(ListItemText, { style: { width: '44px' } }),
                config.issueColumns.map((column) => (React.createElement(ListItemText, { key: column.name, style: { width: column.width } })))),
            React.createElement(ListItem, { disablePadding: true, sx: (theme) => ({
                    width: '100%',
                    display: 'flex',
                    position: 'sticky !important',
                    top: '0',
                    zIndex: '1',
                    '.MuiListItemButton-root': {
                        display: 'flex',
                        flex: 'none',
                        padding: theme.spacing(0),
                        position: 'relative',
                        bgcolor: 'background.default',
                        borderColor: theme.palette.divider,
                        '&:first-of-type': {
                            '.MuiListItemText-root': {
                                paddingLeft: '31px',
                            },
                        },
                        '&:last-of-type': {
                            flex: '1',
                            '.MuiListItemText-root': {
                                paddingRight: theme.spacing(2),
                            },
                        },
                        '.MuiTouchRipple-rippleVisible': {
                            color: 'inherit',
                        },
                        '.MuiListItemText-root': {
                            display: 'flex',
                            cursor: 'pointer',
                            margin: theme.spacing(0),
                            padding: '6px 6px',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            '.MuiTypography-root': {
                                display: 'flex',
                                '&:nth-of-type(1)': {
                                    color: 'text.secondary',
                                    marginRight: '4px',
                                },
                                '&:nth-of-type(2) svg': {
                                    fontSize: '12px',
                                    transition: 'all 300ms',
                                    transform: 'rotateZ(0deg)',
                                },
                            },
                        },
                    },
                }) },
                React.createElement(ListItemButton, { disableTouchRipple: true, style: { minWidth: '44px', minHeight: '30px' } },
                    React.createElement(ListItemText, { disableTypography: true })),
                config.issueColumns.map((column) => (React.createElement(ListItemButton, { key: column.name, onClick: () => onSortClick(column), disableTouchRipple: !Object.prototype.hasOwnProperty.call(column, 'order'), sx: (theme) => ({
                        minWidth: column.width,
                        '.MuiListItemText-root': {
                            display: 'flex',
                            justifyContent: 'space-between',
                            '.MuiTypography-root': {
                                width: 'auto',
                                '&:nth-of-type(2)': Object.assign({ opacity: '0.2', transform: 'rotateZ(0)', transition: 'transform ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms' }, (sortSelectedItem.name === column.name && Object.assign({ opacity: '1' }, (sortSelectedItem.order === 'DESC' && {
                                    transform: 'rotateZ(180deg)',
                                })))),
                            },
                        },
                    }) },
                    React.createElement(ListItemText, { disableTypography: true },
                        React.createElement(Typography, null, column.label),
                        React.createElement(Typography, null, column.order && React.createElement(ArrowUpward, { fontSize: 'small' }))))))),
            children));
    }));
});
IssueListInnerElementType.displayName = 'IssueListInnerElementType';
const IssueListItemRender = ({ data, index, style }) => {
    const { items, selectedItem, onClick } = data;
    const item = items[index];
    return (React.createElement(ListItem, { key: index, disablePadding: true, style: style, selected: item.id === (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.id), onClick: () => onClick(item), sx: (theme) => (Object.assign({ width: '100%', top: `${Number(style.top) + 30}px!important`, userSelect: 'none', borderRadius: '0', '.MuiListItemAvatar-root': {
                minWidth: '20px',
            }, '.MuiAvatar-circular': {
                width: '20px',
                height: '20px',
                bgcolor: alpha(theme.palette.grey.main, 0.12),
                svg: {
                    fontSize: '14px',
                },
            }, '&:hover': {
                '.MuiListItemText-root': {
                    cursor: 'pointer',
                },
            }, '&.Mui-selected': {
                '.MuiListItemText-root': {
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                },
                '.MuiAvatar-circular': {
                    bgcolor: alpha(theme.palette.primary.contrastText, 1),
                },
            }, '.MuiListItemButton-root': {
                '&:first-of-type': {
                    paddingLeft: '7px',
                    paddingTop: '3px',
                    paddingBottom: '3px',
                },
                '&:hover': {
                    bgcolor: 'inherit',
                },
            }, '.MuiListItemText-root': {
                display: 'flex',
                flex: 'none',
                height: '100%',
                margin: theme.spacing(0),
                padding: theme.spacing(0.5, 1),
                alignItems: 'center',
                '&:first-of-type': {
                    paddingLeft: theme.spacing(2),
                    svg: {
                        fontSize: '12px',
                    },
                },
                '&:last-of-type': {
                    flex: '1',
                    paddingRight: theme.spacing(2),
                },
                '.MuiTypography-root': {
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                },
            } }, ((items.length - (items.length - 1 - index)) % 2 === 0 && {
            '& .MuiListItemText-root': {
                bgcolor: 'background.default',
            },
        }))) },
        React.createElement(ListItemText, { style: { width: '44px' } },
            React.createElement(ListItemAvatar, null,
                React.createElement(Avatar, null,
                    React.createElement(QuestionAnswer, { fontSize: 'small', sx: { color: item.status_color } })))),
        config.issueColumns.map((column) => {
            var _a;
            return (React.createElement(ListItemText, { key: column.name, style: { width: column.width } },
                React.createElement(Typography, null, column.format ? column.format(item[column.name]) : String((_a = item[column.name]) !== null && _a !== void 0 ? _a : ''))));
        })));
};
export default IssueList;
